import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, Play, Check } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import { getSysconfig, inferDbamBase } from '../../sysconfig';

const SERVICE_TYPE = 'tts';

// ─── Kernel API base ───────────────────────────────────────────────────────

function getKernelApiBase() {
    const envBase = process.env.REACT_APP_KERNEL_API_BASE;
    if (envBase) return envBase;
    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        if (h === 'localhost' || h === '127.0.0.1') return 'http://localhost:3000/api';
    }
    return 'https://cloudsam-kernel-vm77772tsa-uc.a.run.app/api';
}

// ─── Help Agent Voice Picker ───────────────────────────────────────────────

function HelpAgentVoicePicker() {
    const [voices, setVoices] = useState([]);
    const [selectedVoice, setSelectedVoice] = useState('');
    const [loadingVoices, setLoadingVoices] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [currentVoice, setCurrentVoice] = useState(''); // what's saved in sysconfig

    // Load current setting from sysconfig
    useEffect(() => {
        (async () => {
            try {
                const cfg = await getSysconfig();
                const voice = cfg?.data?.platform?.help_agent_voice || '';
                setCurrentVoice(voice);
                setSelectedVoice(voice);
            } catch (e) {
                console.warn('Failed to load help agent voice setting:', e);
            }
        })();
    }, []);

    // Load available voices from kernel
    useEffect(() => {
        (async () => {
            setLoadingVoices(true);
            try {
                const res = await fetch(`${getKernelApiBase()}/tts/google/voices?languageCode=en-US`);
                if (res.ok) {
                    const data = await res.json();
                    const list = (data.voices || [])
                        .filter(v => v.name && v.name.includes('Chirp3-HD'));
                    // Deduplicate by voice name (API returns one entry per language code)
                    const unique = [...new Map(list.map(v => [v.name, v])).values()]
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setVoices(unique);
                }
            } catch (e) {
                console.warn('Failed to load voices:', e);
            }
            setLoadingVoices(false);
        })();
    }, []);

    const handlePreview = async () => {
        if (!selectedVoice || previewing) return;
        setPreviewing(true);
        try {
            const res = await fetch(`${getKernelApiBase()}/tts/speak`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: 'Hi, I\'m Sam, your SamSurveyor help assistant. How can I help you today?',
                    voice: selectedVoice,
                    context: 'voice_preview',
                }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.success && data.audioData) {
                    const binary = atob(data.audioData);
                    const bytes = new Uint8Array(binary.length);
                    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                    const format = data.audioFormat === 'ogg_opus' ? 'audio/ogg' : 'audio/mp3';
                    const blob = new Blob([bytes], { type: format });
                    const url = URL.createObjectURL(blob);
                    const audio = new Audio(url);
                    audio.onended = () => { URL.revokeObjectURL(url); setPreviewing(false); };
                    audio.onerror = () => { URL.revokeObjectURL(url); setPreviewing(false); };
                    audio.play().catch(() => setPreviewing(false));
                    return;
                }
            }
        } catch (e) {
            console.warn('Preview failed:', e);
        }
        setPreviewing(false);
    };

    const handleSave = async () => {
        if (!selectedVoice) return;
        setSaving(true);
        setSaved(false);
        try {
            const dbamBase = process.env.REACT_APP_SAM_DBAM_URL || inferDbamBase();
            // Read current platform config, merge in help_agent_voice
            const cfg = await getSysconfig();
            const currentPlatform = cfg?.data?.platform || {};
            const updatedPlatform = { ...currentPlatform, help_agent_voice: selectedVoice };

            await fetch(`${dbamBase.replace(/\/+$/, '')}/dbam/v1/config`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ key: 'platform', value: updatedPlatform }),
            });
            setCurrentVoice(selectedVoice);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (e) {
            console.error('Failed to save help agent voice:', e);
        }
        setSaving(false);
    };

    // Extract short display name from voice ID
    const displayName = (voiceId) => {
        // "en-US-Chirp3-HD-Achernar" → "Achernar"
        const parts = voiceId.split('-');
        return parts.length >= 5 ? parts.slice(4).join('-') : voiceId;
    };

    return (
        <div style={{ marginTop: 40 }}>
            <h3 style={styles.sectionTitle}>Help Agent Voice</h3>
            <p style={styles.subtitle}>
                Choose the voice Sam uses when speaking in the Help Agent popup.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
                <select
                    value={selectedVoice}
                    onChange={e => { setSelectedVoice(e.target.value); setSaved(false); }}
                    style={{
                        ...styles.input,
                        flex: 1,
                        maxWidth: 360,
                        cursor: 'pointer',
                    }}
                    disabled={loadingVoices}
                >
                    <option value="">
                        {loadingVoices ? 'Loading voices…' : '— Select a voice —'}
                    </option>
                    {voices.map(v => (
                        <option key={v.name} value={v.name}>
                            {displayName(v.name)}
                            {v.name === currentVoice ? ' (current)' : ''}
                        </option>
                    ))}
                </select>

                <button
                    onClick={handlePreview}
                    disabled={!selectedVoice || previewing}
                    style={{
                        ...styles.previewBtn,
                        opacity: (!selectedVoice || previewing) ? 0.4 : 1,
                    }}
                    title="Preview this voice"
                >
                    <Play size={14} style={{ marginRight: 6 }} />
                    {previewing ? 'Playing…' : 'Preview'}
                </button>

                <button
                    onClick={handleSave}
                    disabled={saving || !selectedVoice || selectedVoice === currentVoice}
                    style={{
                        ...styles.saveBtn,
                        opacity: (saving || !selectedVoice || selectedVoice === currentVoice) ? 0.4 : 1,
                    }}
                >
                    {saving ? 'Saving…' : saved ? (
                        <><Check size={14} style={{ marginRight: 4 }} /> Saved</>
                    ) : 'Save'}
                </button>
            </div>

            {currentVoice && (
                <div style={{ marginTop: 10, fontSize: 13, color: '#B4C0D3' }}>
                    Current: <strong style={{ color: '#E2E8F0' }}>{displayName(currentVoice)}</strong>
                </div>
            )}
        </div>
    );
}

// ─── Main Section ──────────────────────────────────────────────────────────

export function AccountVoicesSection() {
    const {
        serviceConfigs, systemDefaults, credentials, loading,
        loadServiceConfigs, loadSystemDefaults, saveServiceConfig,
        removeServiceConfig, addCredential, loadCredentials,
    } = useOrg();

    const [mode, setMode] = useState('inherit');
    const [form, setForm] = useState({ voice_name: '', language: 'en-US', service_account_json: '' });
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const config = serviceConfigs.find(c => c.service_type === SERVICE_TYPE);
    const sysDefault = systemDefaults.find(d => d.service_type === SERVICE_TYPE);

    useEffect(() => {
        loadServiceConfigs();
        loadSystemDefaults();
        loadCredentials();
    }, [loadServiceConfigs, loadSystemDefaults, loadCredentials]);

    useEffect(() => {
        if (config) {
            setMode('custom');
            setForm(f => ({
                ...f,
                voice_name: config.model || '',
                language: config.endpoint || 'en-US',
                service_account_json: '',
            }));
        } else {
            setMode('inherit');
        }
    }, [config]);

    const handleModeChange = useCallback(async (newMode) => {
        setMode(newMode);
        if (newMode === 'inherit' && config) {
            await removeServiceConfig(SERVICE_TYPE);
        }
    }, [config, removeServiceConfig]);

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        try {
            await saveServiceConfig(SERVICE_TYPE, {
                provider: 'google_cloud_tts',
                model: form.voice_name,
                endpoint: form.language,
            });
            if (form.service_account_json) {
                await addCredential({
                    service_type: SERVICE_TYPE,
                    label: 'Google Cloud TTS Service Account',
                    credential_data: { service_account_json: form.service_account_json },
                });
            }
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch { /* error set by context */ }
        setSaving(false);
    };

    const existingKey = credentials.find(c => c.service_type === SERVICE_TYPE);

    return (
        <div>
            <h2 style={styles.title}>Voice / TTS Configuration</h2>
            <p style={styles.subtitle}>
                Configure text-to-speech for audio survey responses.
            </p>

            <div style={styles.toggleGroup}>
                <label style={styles.radioLabel}>
                    <input
                        type="radio" name="tts-mode" value="inherit"
                        checked={mode === 'inherit'}
                        onChange={() => handleModeChange('inherit')}
                        style={styles.radio}
                    />
                    <span><strong>Inherit System Default</strong></span>
                </label>
                <label style={styles.radioLabel}>
                    <input
                        type="radio" name="tts-mode" value="custom"
                        checked={mode === 'custom'}
                        onChange={() => handleModeChange('custom')}
                        style={styles.radio}
                    />
                    <span><strong>Use Custom TTS</strong></span>
                </label>
            </div>

            {mode === 'inherit' && (
                <div style={styles.inheritBox}>
                    <div style={styles.inheritIcon}><Volume2 size={32} color="#A5B4FC" /></div>
                    <div>
                        <div style={styles.inheritLabel}>System Default</div>
                        <div style={styles.inheritValue}>
                            {sysDefault?.provider || 'Google Cloud TTS'} — {sysDefault?.model || 'en-US-Wavenet-D'}
                        </div>
                        <div style={{
                            ...styles.inheritStatus,
                            color: sysDefault?.inheritable !== false ? '#34D399' : '#F87171',
                        }}>
                            {sysDefault?.inheritable !== false ? '● Available' : '● Not available — contact admin'}
                        </div>
                    </div>
                </div>
            )}

            {mode === 'custom' && (
                <div style={styles.customForm}>
                    <div style={styles.field}>
                        <label style={styles.label}>Voice Name</label>
                        <input
                            value={form.voice_name}
                            onChange={e => setForm(f => ({ ...f, voice_name: e.target.value }))}
                            placeholder="e.g. en-US-Wavenet-D"
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>Language Code</label>
                        <input
                            value={form.language}
                            onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
                            placeholder="e.g. en-US"
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.field}>
                        <label style={styles.label}>
                            Service Account JSON {existingKey && <span style={styles.existingBadge}>● Credential stored</span>}
                        </label>
                        <textarea
                            value={form.service_account_json}
                            onChange={e => setForm(f => ({ ...f, service_account_json: e.target.value }))}
                            placeholder={existingKey ? 'Leave blank to keep current credential' : 'Paste Google Cloud service account JSON'}
                            rows={6}
                            style={{ ...styles.input, fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                        />
                    </div>

                    <div style={styles.actions}>
                        <button
                            onClick={handleSave}
                            disabled={saving || loading || !form.voice_name}
                            style={{
                                ...styles.saveBtn,
                                opacity: (saving || loading || !form.voice_name) ? 0.5 : 1,
                            }}
                        >
                            {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Configuration'}
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Help Agent Voice ───────────────────────────────── */}
            <HelpAgentVoicePicker />
        </div>
    );
}

const styles = {
    title: { fontSize: 20, fontWeight: 600, color: '#E2E8F0', margin: '0 0 4px' },
    sectionTitle: { fontSize: 16, fontWeight: 600, color: '#E2E8F0', margin: '0 0 4px' },
    subtitle: { fontSize: 13, color: '#C3CDDC', margin: '0 0 24px' },
    toggleGroup: { display: 'flex', gap: 24, marginBottom: 24 },
    radioLabel: { display: 'flex', alignItems: 'center', gap: 8, color: '#CBD5E1', fontSize: 14, cursor: 'pointer' },
    radio: { accentColor: '#818CF8' },
    inheritBox: {
        display: 'flex', alignItems: 'center', gap: 16,
        background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: 10, padding: '20px 24px',
    },
    inheritIcon: { fontSize: 32 },
    inheritLabel: { fontSize: 12, color: '#C3CDDC', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 },
    inheritValue: { fontSize: 16, fontWeight: 600, color: '#E2E8F0' },
    inheritStatus: { fontSize: 13, marginTop: 4 },
    customForm: { display: 'flex', flexDirection: 'column', gap: 16 },
    field: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: { fontSize: 15, fontWeight: 600, color: '#E2E8F0' },
    input: {
        background: '#0F172A', border: '1px solid #334155', borderRadius: 8,
        padding: '10px 14px', color: '#E2E8F0', fontSize: 14, outline: 'none',
    },
    existingBadge: { color: '#34D399', fontSize: 11, marginLeft: 8, fontWeight: 400 },
    actions: { display: 'flex', justifyContent: 'flex-end', marginTop: 8 },
    saveBtn: {
        background: '#6366F1', color: '#fff', border: 'none', borderRadius: 8,
        padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    },
    previewBtn: {
        background: 'rgba(99, 102, 241, 0.12)', color: '#A5B4FC', border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
        display: 'flex', alignItems: 'center',
    },
};

